import React, { FC } from 'react';
import { useWatch } from 'react-hook-form';

import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { Checkbox } from '../../../../components/checkbox/index';
import { FormDatePicker } from '../../../../components/date-picker/index';
import { DropDown } from '../../../../components/dropdown/index';
import { FormInput } from '../../../../components/input/index';
import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { useAppMutation, useDebounce, useFormat, useLocale } from '../../../../hooks/index';
import { ApiService } from '../../../../services/index';
import { ChildrenFormProps } from '../modals.types';
import { FormSchema, formSchemaStepOne } from '../modals.validation';

export type StepOneProps = ChildrenFormProps;

export const StepOne: FC<StepOneProps> = ({
  control,
  handleCloseModal,
  nextFormStep,
  setFormData,
  lockMode,
  setError,
  clearErrors,
  filedState,
}) => {
  const { stepOne } = useWatch<FormSchema>({ control });
  const { success, error } = formSchemaStepOne.safeParse(stepOne);
  const { format } = useFormat();
  const { messagesLocale } = useLocale();

  const { mutate } = useAppMutation(
    [QueryKey.SHARE_CLASS_NAME_CHECK, { name: stepOne?.name || '' }],
    {
      mutationFn: async (name: string) =>
        await ApiService.get<{ isInUse: boolean }>({
          endpoint: BackendRoute.SHARE_CLASSES,
          routePath: ['check-is-name-in-use', name],
        }),
      onSuccess: ({ isInUse }) => {
        if (isInUse) {
          setError('stepOne.name', {
            message: messagesLocale['NAME_SHOULD_BE_UNIQUE'],
            type: 'validate',
            types: { required: true },
          });
          return;
        }

        const currentErrors = error?.issues?.filter((e) => e.path[0] === 'name') || [];

        const stillHasOtherErrors = currentErrors.some(
          (e) => e.code !== 'custom' && e.path[0] === 'name',
        );

        if (!stillHasOtherErrors) {
          clearErrors('stepOne.name');
        } else {
          setError('stepOne.name', {
            message: currentErrors.find((e) => e.code !== 'custom')?.message || '',
            type: currentErrors.find((e) => e.code !== 'custom')?.code || 'manual',
          });
        }
      },
      defaultErrorHandling: false,
    },
  );

  const isValid = success && !filedState('stepOne').invalid;

  const { debounceCallback } = useDebounce<string>({
    callback: (name) => name && mutate(name),
    debounce: 300,
  });

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4 rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex w-full items-start gap-4">
          <FormInput
            control={control}
            name="stepOne.name"
            onChange={debounceCallback}
            placeholder={<AppFormattedMessage id="SHARE_CLASS_NAME" />}
            wrapperClassName="w-full"
          />

          <FormDatePicker
            iconColor="#98A2B3"
            inputValue={stepOne?.creationDate ? format(stepOne?.creationDate, 'dd/MM/yyyy') : ''}
            inputWrapperClassName="cursor-pointer"
            onSelect={(date) => setFormData('stepOne.creationDate', date)}
            placeholder={<AppFormattedMessage id="CREATION_DATE" />}
            value={stepOne?.creationDate}
            wrapperClassName="w-full"
          />
        </div>
        <div className="flex w-full flex-col gap-3 rounded-md bg-gray-50 px-[2px]">
          <span className="px-4 pt-3 text-sm font-[450] text-gray-700">
            <AppFormattedMessage id="GENERAL" />
          </span>
          <div className="flex w-full flex-col divide-y-[1px] divide-gray-100 rounded-md bg-white shadow-sm">
            <div className="flex h-20 w-full items-center justify-between gap-4 px-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={stepOne?.votingRight?.enabled}
                  onChange={(state) =>
                    state
                      ? setFormData('stepOne.votingRight.enabled', state)
                      : setFormData('stepOne.votingRight', undefined)
                  }
                />
                <span className="text-sm font-[450] text-gray-700">
                  <AppFormattedMessage id="VOTING_RIGHT" />
                </span>
              </div>
              {stepOne?.votingRight?.enabled && (
                <FormInput
                  className="w-[190px]"
                  control={control}
                  isShownErrorMessage={false}
                  maxDecimalPlaces={2}
                  name="stepOne.votingRight.value"
                  placeholder={<AppFormattedMessage id="MULTIPLY_VALUE" />}
                />
              )}
            </div>
            <div className="flex h-20 w-full items-center justify-between gap-4 px-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={stepOne?.conversionRatio?.enabled}
                  onChange={(state) =>
                    state
                      ? setFormData('stepOne.conversionRatio.enabled', state)
                      : setFormData('stepOne.conversionRatio', undefined)
                  }
                />
                <span className="text-nowrap text-sm font-[450] text-gray-700">
                  <AppFormattedMessage id="CONVERSION_RATIO" />
                </span>
              </div>
              {stepOne?.conversionRatio?.enabled && (
                <FormInput
                  className="w-[190px]"
                  control={control}
                  isShownErrorMessage={false}
                  maxDecimalPlaces={2}
                  name="stepOne.conversionRatio.value"
                  placeholder={<AppFormattedMessage id="MULTIPLY_VALUE" />}
                />
              )}
            </div>
            <div className="flex h-20 w-full items-center gap-4 px-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={stepOne?.dividendRightEnabled}
                  onChange={(state) => {
                    setFormData('stepOne.dividendRightEnabled', state);
                  }}
                />
                <span className="text-sm font-[450] text-gray-700">
                  <AppFormattedMessage id="DIVIDEND_RIGHT" />
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-col divide-y-[1px] divide-gray-100 rounded-lg bg-gray-50">
          <DropDown
            answer={[
              messagesLocale['NAMING_SHARE_CLASS_RECOMMENDATION_1'],
              messagesLocale['NAMING_SHARE_CLASS_RECOMMENDATION_2'],
              messagesLocale['NAMING_SHARE_CLASS_RECOMMENDATION_3'],
            ]}
            question={<AppFormattedMessage id="HOW_TO_NAME_A_SHARE_CLASS" />}
          />
          <DropDown
            answer={messagesLocale['VOTING_RIGHTS_EXPLANATION']}
            question={<AppFormattedMessage id="WHAT_ARE_VOTING_RIGHTS" />}
          />
          <DropDown
            answer={messagesLocale['DIVIDEND_EXPLANATION']}
            question={<AppFormattedMessage id="WHAT_ARE_DIVIDEND_RIGHTS" />}
          />
          <DropDown
            answer={messagesLocale['CONVERSION_RATION_EXPLANATION']}
            question={<AppFormattedMessage id="WHAT_IS_CONVERSION_RATIO" />}
          />
        </div>
      </div>
      <div className="flex h-9 w-full justify-between gap-3 px-6">
        <Button
          className="w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700"
          onClick={handleCloseModal}
          styleType="NONE"
          type="button"
        >
          <AppFormattedMessage id="CANCEL" />
        </Button>
        <div className="flex gap-3">
          <Button
            className="h-full w-fit px-4 py-[6px] text-sm font-[550] text-white"
            disabled={!isValid}
            onClick={nextFormStep}
            type="button"
          >
            {lockMode ? <AppFormattedMessage id="UPDATE" /> : <AppFormattedMessage id="NEXT" />}
          </Button>
        </div>
      </div>
    </>
  );
};
